import { ReactNode } from "react"
import { Dialog as RMWCDialog, DialogProps as RMWCDialogProps } from "@rmwc/dialog"
import { Property } from "csstype"
import { DialogContent, DialogTitle, DialogActions } from "./components"
import "@rmwc/dialog/styles"
import { Box } from "../Box"
import { Button } from "../Button"
import { Flex } from "../Flex"
import { SubTitle, Title } from "../Typography"
import { Cross1Icon } from "../Icons"
import * as styles from "./dialogStyle"
import { CSSFn } from "../styles"

type DialogProps = {
  title?: string
  subTitle?: string
  children: ReactNode
  closeDialog: () => void
  dataTestId?: string
  okButtonDataTestId?: string
  closeButtonDataTestId?: string
  hideActions?: boolean
  rootCss?: CSSFn
  contentCss?: CSSFn
  primaryButton?: {
    action: () => void
    text: string
    disabled?: boolean
    dataTestID?: string
  }
  secondaryButton?: {
    action?: () => void
    text?: string
    dataTestID?: string
  }
  fullWidth?: boolean
  minHeight?: Property.MinHeight
  customWidth?: CSSFn
} & Omit<RMWCDialogProps, "onClose">

export const Dialog = ({
  title,
  subTitle,
  children,
  hideActions,
  rootCss,
  contentCss,
  closeDialog,
  primaryButton,
  secondaryButton,
  fullWidth,
  minHeight,
  dataTestId,
  okButtonDataTestId,
  closeButtonDataTestId,
  customWidth,
  ...props
}: DialogProps) => (
  <Box css={styles.root(rootCss)}>
    <RMWCDialog
      onClose={() => {
        closeDialog()
        setTimeout(() => window.scrollTo(0, 0), 50)
      }}
      {...props}
      data-testid={dataTestId}
    >
      <Box css={styles.titleWrap}>
        {title && (
          <DialogTitle>
            <Title>{title}</Title>
            {subTitle && <SubTitle css={styles.subTitle}>{subTitle}</SubTitle>}
          </DialogTitle>
        )}
        <Button data-testid={closeButtonDataTestId} css={styles.closeIcon} variant="reset" onClick={closeDialog}>
          <Cross1Icon />
        </Button>
      </Box>
      <DialogContent css={contentCss}>
        <Flex
          flexDirection="column"
          width={fullWidth ? `98vw` : `637px`}
          maxWidth={fullWidth ? `auto` : `95vw`}
          minHeight={minHeight}
          css={customWidth}
        >
          {` `}
          {children}
        </Flex>
      </DialogContent>
      {!hideActions && (
        <DialogActions>
          {primaryButton ? (
            <>
              <Button
                variant="outline"
                type="button"
                onClick={secondaryButton?.action || closeDialog}
                data-testid={secondaryButton?.dataTestID}
              >
                {secondaryButton?.text || `Cancel`}
              </Button>
              <Button
                disabled={primaryButton?.disabled}
                onClick={primaryButton.action}
                data-testid={primaryButton.dataTestID}
              >
                {primaryButton.text}
              </Button>
            </>
          ) : (
            <Button onClick={closeDialog} data-testid={okButtonDataTestId}>
              OK
            </Button>
          )}
        </DialogActions>
      )}
    </RMWCDialog>
  </Box>
)

Dialog.defaultProps = {
  title: undefined,
  subTitle: undefined,
  hideActions: false,
  rootCss: undefined,
  contentCss: undefined,
  dataTestId: undefined,
  okButtonDataTestId: undefined,
  primaryButton: undefined,
  secondaryButton: undefined,
  minHeight: undefined,
  fullWidth: false,
  closeButtonDataTestId: undefined,
  customWidth: undefined,
}
